import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { useVeri, tarihSaat } from "@/lib/veri";
import { Band, Bos, Buton, Cip, Etiket, ExcelButonu, Hata, Kutu, Kv, Sayfa, tabloBas, tabloHucre } from "@/components/parcalar";
import { cevir } from "@/lib/dil";
import {
  DURUM_TONU,
  ICERIK_DURUMLARI,
  basHarfBuyuk,
  donusturucuRozeti,
  durumAdi,
  durumSayilari,
  gecenMetni,
  icerikDurumu,
  kisaTarihSaat,
  saat,
  sirali,
  siraliYenile,
  sureMetni,
  type Donusum,
  type IcerikDurumu,
} from "@/lib/mrb-toplu";
import { MrbTopluYukleme } from "./MrbTopluYukleme";

/** Dönüşüm durumunu gösteren alanlar (liste satırı ve detaydaki `mrb` nesnesi, U-050). */
export interface DonusumBilgisi {
  id: string;
  icerik: { ts: string; kaynak?: string | null } | null;
  donusum: Donusum | null;
  siraNo: number | null;
  tutmaBaslangic: string | null;
  donusumHata: string | null;
  donusumHataTs: string | null;
}

interface MrbSatiri extends DonusumBilgisi {
  sn: string;
  dosya: string;
  durum: string;
  riserRn: string | null;
  /** D-061: bağ, riserin söküm kaydındaki SN'den bu MRB yüklenince kuruldu (sonradan bağ); değilse null. */
  sokumBagi: { rn: string } | null;
  gelisTs: string;
  icerik: { id: string; dosya: string; ts: string; yukleyen: string; assemblyPn: string | null; model: string; kaynak: string | null } | null;
}

interface Donusturucu {
  sonBaglanti: string | null;
  anahtarAd: string | null;
  aktifAnahtarVar: boolean;
  donusturulen: { sn: string; tutmaBaslangic: string; tutmaBitis: string } | null;
  sirada: number;
  hatali: number;
}

interface Mrb {
  beklenen: number;
  gelen: MrbSatiri[];
  eksik: number;
  icerikli: number;
  talep: boolean;
  donusturucu: Donusturucu;
}

/** Dönüştürücü durumu canlı görünsün diye liste bu aralıkla kendini yeniler. */
const YENILEME_MS = 30_000;

/** Kuyruk işlemi: sıraya ekle (none ya da failed'dan "Retry") ya da sıradan çıkar. */
export const siraIslemi = (id: string, islem: "ekle" | "cikar") => api.post(`/mrb/${id}/sira`, { islem });

/**
 * "Content excel" durumu (D-060): uploaded (by hand · converter ve zamanı) · converting (başlama saati) · queued (sıra, + Remove from queue)
 * · failed (+ Retry, altında zaman ve sebep) · none (+ Add to queue). Envanter tablosu ve kayıt sayfası aynı bileşeni kullanır.
 */
export function DonusumDurumu({ m, islem, mesgul }: { m: DonusumBilgisi; islem: (islem: "ekle" | "cikar") => void; mesgul?: boolean }) {
  const d = icerikDurumu(m);
  const etiket = <Etiket tone={DURUM_TONU[d]}>{d === "sirada" && m.siraNo ? cevir("queued · {0}", sirali(m.siraNo)) : durumAdi(d)}</Etiket>;
  const not = (metin: string) => <span className="text-xs text-ink-3">{metin}</span>;
  // Envanterde satır tıklaması kaydı açar; düğme açmasın.
  const dugme = (ad: string, i: "ekle" | "cikar") => <span onClick={(e) => e.stopPropagation()}><Buton kucuk islem="mrb" mesgul={mesgul} onClick={() => islem(i)}>{ad}</Buton></span>;
  switch (d) {
    case "yuklendi":
      return <>{etiket} {not(m.icerik?.kaynak === "donusturucu" ? `${cevir("converter")} · ${kisaTarihSaat(m.icerik.ts)}` : cevir("by hand"))}</>;
    case "donusturuluyor":
      return <>{etiket}{m.tutmaBaslangic && <> {not(cevir("since {0}", saat(m.tutmaBaslangic)))}</>}</>;
    case "sirada":
      return <>{etiket} {dugme(cevir("Remove from queue"), "cikar")}</>;
    case "hata": {
      const ayrinti = [m.donusumHataTs ? kisaTarihSaat(m.donusumHataTs) : null, m.donusumHata ? cevir(m.donusumHata) : null].filter(Boolean).join(" · ");
      return (
        <>
          {etiket} {dugme(cevir("Retry"), "ekle")}
          {ayrinti && <div className="mt-1 max-w-lg text-xs text-bad">{ayrinti}</div>}
        </>
      );
    }
    case "yok":
      return <>{etiket} {dugme(cevir("Add to queue"), "ekle")}</>;
  }
}

/** D-060 "MRB converter": son bağlantı (sunucu saatiyle), anahtar, şu an dönüştürülen ve tutma süresi, sırada ve hatalı sayısı. */
function DonusturucuKutusu({ d, simdi }: { d: Donusturucu; simdi: number }) {
  const r = donusturucuRozeti(d, simdi);
  const rozet =
    r.tur === "bagli" ? <Etiket tone="ok">{cevir("connected")}</Etiket>
    : r.tur === "kopuk" ? <Etiket tone={r.uyari ? "warn" : "neutral"}>{cevir("not connected for {0}", sureMetni(r.gecen ?? 0))}</Etiket>
    : <Etiket>{cevir("not connected yet")}</Etiket>;
  return (
    <Kutu baslik={cevir("MRB converter")} aksiyon={rozet}>
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kv ad={cevir("Last connected")}>{d.sonBaglanti ? <><b>{tarihSaat(d.sonBaglanti)}</b> <span className="text-ink-3">· {gecenMetni(r.gecen ?? 0)}</span></> : cevir("never")}</Kv>
        <Kv ad={cevir("Key")}>{d.anahtarAd ? <>{d.anahtarAd} <span className="text-ink-3">· {cevir("created by System Administrator")}</span></> : "—"}</Kv>
        <Kv ad={cevir("Now converting")}>
          {d.donusturulen ? <><b className="font-mono">{d.donusturulen.sn}</b> <span className="text-ink-3">· {cevir("since {0}", saat(d.donusturulen.tutmaBaslangic))} · {cevir("held until {0}", saat(d.donusturulen.tutmaBitis))}</span></> : "—"}
        </Kv>
        <Kv ad={cevir("Waiting")}><b className="tnum">{d.sirada}</b> {cevir("queued")} <span className="text-ink-3">·</span> <b className="tnum">{d.hatali}</b> {cevir("failed")}</Kv>
      </div>
      {!d.aktifAnahtarVar && <Band tip="not" className="mt-3">{cevir("No active converter key. A System Administrator creates one in Admin.")}</Band>}
      {r.uyari && (
        <Band tip="not" className="mt-3">
          {d.sirada === 1
            ? cevir("The converter has not connected for {0} and 1 MRB is waiting. The converter computer may be off or asleep.", sureMetni(r.gecen ?? 0))
            : cevir("The converter has not connected for {0} and {1} MRBs are waiting. The converter computer may be off or asleep.", sureMetni(r.gecen ?? 0), d.sirada)}
        </Band>
      )}
      <Band tip="bilgi" className="mt-3">
        {cevir("The converter runs on a computer outside OASIS. While that computer is on, it checks this queue every 15 minutes and converts one document at a time; Disassembly reports waiting for conversion are taken first. An MRB is converted to the content excel with the MRB engine. The excel it delivers is")} <b>{cevir("linked to the MRB right away")}</b>{cevir(", with the same checks as a manual upload; an excel that fails the checks is not linked and the MRB shows as failed, with the reason. An MRB that already has a content excel is never converted again. If the computer sleeps during a conversion, the MRB goes back to the queue when its hold time runs out.")}
      </Band>
    </Kutu>
  );
}

export function Mrb() {
  const { veri, setVeri, hata } = useVeri<Mrb>("/mrb");
  const nav = useNavigate();
  const [islem, setIslem] = useState<string | null>(null);
  const [pencere, setPencere] = useState(false);
  const [suzgec, setSuzgec] = useState<IcerikDurumu | null>(null);
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [simdi, setSimdi] = useState(() => Date.now());

  /**
   * Sessiz yenileme: hata ekranı açmaz, yalnız veriyi yerine koyar. Yükleme penceresinin satırları kendi bileşeninde durduğu için
   * pencere açıkken ya da yükleme sürerken yenilemek listeyi bozmaz (D-060). Aynı anda uçan yenilemelerden yalnız en son
   * başlatılanın yanıtı yazılır: arka plan yenilemesinin geç dönen yanıtı bir işlemin sonucunu eski veriyle ezmez.
   */
  const getir = useMemo(() => siraliYenile(() => api.get<Mrb>("/mrb"), setVeri), [setVeri]);
  const tazele = useCallback(async () => {
    try {
      await getir();
    } catch {
      // Arka plan yenilemesi: bir sonraki turda yeniden denenir.
    }
    setSimdi(Date.now());
  }, [getir]);

  useEffect(() => {
    const t = window.setInterval(() => {
      if (document.visibilityState !== "hidden") void tazele();
    }, YENILEME_MS);
    return () => window.clearInterval(t);
  }, [tazele]);

  const sira = async (id: string, i: "ekle" | "cikar") => {
    setIslem(null);
    setMesgul(id);
    try {
      await siraIslemi(id, i);
    } catch (e) {
      setIslem(e instanceof Error ? e.message : cevir("Operation failed"));
    } finally {
      setMesgul(null);
      await tazele();
    }
  };

  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;
  const sayilar = durumSayilari(veri.gelen);
  const gorunen = suzgec ? veri.gelen.filter((m) => icerikDurumu(m) === suzgec) : veri.gelen;
  return (
    <Sayfa ust="0.2 · E-28" baslik={cevir("MRB intake — unit based inventory")} alt={<>{cevir("MRBs")} <b>{cevir("by serial number")}</b> {cevir("arrive, not by riser number; RN↔SN mapping")} <b>{cevir("in Disassembly")}</b> {cevir("established. Here Duvar 1 (Identity) is intentionally not applied; only unit tracking. Upload: Quality Lead.")}</>}>
      <Hata mesaj={islem} />
      <DonusturucuKutusu d={veri.donusturucu} simdi={simdi} />
      <Kutu baslik={cevir("MRB inventory")} aksiyon={<ExcelButonu gorunum="mrb" />}>
        <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
          {([[cevir("Expected"), veri.beklenen], [cevir("Arrived"), veri.gelen.length], [cevir("Missing"), veri.eksik], [cevir("Content excel"), `${veri.icerikli}/${veri.gelen.length}`]] as const).map(([a, n]) => <div key={a} className="rounded-md bg-surface-2 p-3"><div className="text-xs text-ink-3">{a}</div><div className="text-2xl font-semibold tnum">{n}</div></div>)}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {([null, ...ICERIK_DURUMLARI] as const).map((k) => (
            <button key={k ?? "hepsi"} type="button" className="rounded-full" aria-pressed={suzgec === k} style={suzgec === k ? { outline: "2px solid var(--brand-orange)" } : undefined} onClick={() => setSuzgec(k)}>
              <Etiket tone={k ? DURUM_TONU[k] : "neutral"}>{k ? basHarfBuyuk(durumAdi(k)) : cevir("All")} {k ? sayilar[k] : veri.gelen.length}</Etiket>
            </button>
          ))}
        </div>
        <div className="scroll-thin overflow-x-auto"><table className="mt-3 w-full min-w-[760px]"><thead><tr className="border-b border-line"><th className={tabloBas}>Assembly SN</th><th className={tabloBas}>{cevir("File")}</th><th className={tabloBas}>{cevir("Riser mapping")}</th><th className={tabloBas}>{cevir("Content excel")}</th><th /></tr></thead>
          <tbody>{gorunen.map((m) => (
            <tr key={m.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface-2" onClick={() => void nav({ to: "/mrb/$id", params: { id: m.id } })}>
              <td className={tabloHucre}><b className="font-mono">{m.sn}</b></td>
              <td className={tabloHucre}><Cip>{m.dosya}</Cip></td>
              <td className={tabloHucre}>{m.riserRn ? <Etiket tone="ok">{m.riserRn}</Etiket> : <Etiket>{cevir("will be established in Disassembly")}</Etiket>}{m.sokumBagi && <div className="mt-1 text-xs text-ink-3">{cevir("linked when this MRB was uploaded · SN on the Disassembly form of {0}", m.sokumBagi.rn)}</div>}</td>
              <td className={tabloHucre}><DonusumDurumu m={m} mesgul={mesgul === m.id} islem={(i) => void sira(m.id, i)} /></td>
              <td className={tabloHucre}><Link to="/mrb/$id" params={{ id: m.id }} className="text-sm font-semibold text-orange" onClick={(e) => e.stopPropagation()}>{cevir("Open")}</Link></td>
            </tr>
          ))}
            {!veri.gelen.length && <tr><td colSpan={5}><Bos>{cevir("No MRB uploaded yet.")}</Bos></td></tr>}
            {veri.gelen.length > 0 && !gorunen.length && <tr><td colSpan={5}><Bos>{cevir("No MRB in this state.")}</Bos></td></tr>}</tbody></table></div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Buton islem="mrb" tur="birincil" onClick={() => setPencere(true)}>{cevir("Upload MRBs")}</Buton>
          <Buton islem="mrb" onClick={() => api.indir("/mrb/sablon.xlsx")}>{cevir("Empty input template")}</Buton>
          {veri.eksik > 0 && !veri.talep && <Buton islem="mrb" tur="tehlike" onClick={async () => { await api.post("/mrb/talep"); await tazele(); }}>{cevir("Missing")} {veri.eksik} {cevir("Open request record for MRB")}</Buton>}
        </div>
        {veri.talep && <Band tip="not" className="mt-3">{cevir("Request record opened: expected MRBs from TP-OTC/OSI are tracked. Missing MRB DOES NOT BLOCK physical work; riser progresses with DOKUMAN-EKSIK flag and is caught at package gate (E-32).")}</Band>}
        <Band tip="bilgi" className="mt-3">{cevir("Upload one or many MRB PDFs at once, with or without their content excels. SN is read from the file name (e.g.")} <code>MRB_1306721-1.pdf</code>{cevir("); if it cannot be read, you enter it on that file's row. A PDF uploaded with its content excel is complete; a PDF uploaded alone joins the converter queue. If the same file is uploaded twice, it is caught by SHA match and no duplicate record is opened.")}</Band>
        <Band tip="bilgi" className="mt-2">{cevir("Structured form of PDF")} <b>{cevir("content excel")}</b> {cevir("is uploaded together with the PDF or later, from the MRB record's own page. It is the input model produced by the MRB engine (MRB_<SN>-input-EN.xlsx), five pages:")} <b>IDENTITY · DECISIONS · CK_MAP · DOC_INDEX · COMPONENT_TREE</b>{cevir(". Old v3 files (PIECE_LIST · MASTERDATA · CONFLICTS) are not read. A file not matching the template is rejected with the reason, and in that case the PDF is not saved either. The Disassembly old ID check reads the Identity and C&K map (and the COMPONENT_TREE page, if the file has one).")}</Band>
      </Kutu>

      {pencere && <MrbTopluYukleme onKapat={() => { setPencere(false); void tazele(); }} onBitti={() => void tazele()} />}
    </Sayfa>
  );
}
